/**
 * Euro/cent conversion for the receipt amount field.
 *
 * The column is cents, the field is euro. The AI extraction occasionally hands the amount back as
 * a string ("1.234,56"), which a number input refuses to render — the field then looks empty while
 * RHF keeps submitting the raw text, and the store fails with an integer error on a blank field.
 * Anything unparsable therefore becomes an empty field rather than a value silently sent along.
 */
export const centsToAmountInput = (value: unknown): number | '' => {
    if (null === value || undefined === value || '' === value) return '';

    const cents = Number(value);

    return Number.isFinite(cents) ? cents / 100 : '';
};

/** Reads a typed amount the German way too: "1234,56", "1.234,56" and "1.234" all work. */
export const parseAmountInput = (value: unknown): number | null => {
    if (null === value || undefined === value || '' === value) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    let text = String(value).replace(/\s/g, '');
    if (text === '') return null;

    const comma = text.lastIndexOf(',');
    const dot = text.lastIndexOf('.');

    if (comma > dot) {
        text = text.replace(/\./g, '').replace(',', '.');
    } else if (comma >= 0) {
        text = text.replace(/,/g, '');
    } else if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) {
        text = text.replace(/\./g, '');
    }

    if (!/^-?\d*\.?\d*$/.test(text)) return null;

    const amount = Number(text);

    return Number.isFinite(amount) ? amount : null;
};

export const amountInputToCents = (value: unknown): number | null => {
    const amount = parseAmountInput(value);
    if (null === amount) return null;

    const cents = Math.round(amount * 100);

    return Number.isFinite(cents) ? cents : null;
};

/** Cents as the text an amount field shows, with a decimal comma. */
export const centsToAmountText = (value: unknown): string => {
    const amount = centsToAmountInput(value);

    return amount === '' ? '' : amount.toFixed(2).replace('.', ',');
};

/** A filled amount field that does not hold a positive amount. */
export const isInvalidAmountInput = (value: unknown, allowZero = false): boolean => {
    if (null === value || undefined === value || '' === value) return false;

    const cents = amountInputToCents(value);

    return null === cents || cents < 0 || (!allowZero && cents === 0);
};
